import React, { useState, useEffect, useRef } from 'react';
import { searchSongsByTitle } from '../services/songService';
import { Link, useNavigate } from 'react-router-dom';
import useFirestoreData from '../hooks/useFirestoreData';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const SearchUsers = () => {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Use live users from Firestore hook so each user has doc.id
    const { data: liveUsers, loading: liveLoading } = useFirestoreData('users');
    const abortRef = useRef({ cancelled: false });

    useEffect(() => {
        const handler = setTimeout(async () => {
            setLoading(true);
            try {
                let userRes = [];
                let songRes = [];

                // Prefer client-side filtering on liveUsers (has doc.id)
                if (!term.trim()) {
                    userRes = liveUsers || [];
                } else {
                    const qLower = term.toLowerCase();
                    userRes = (liveUsers || []).filter(u => {
                        const name = (u.name || u.displayName || u.fullName || '').toLowerCase();
                        return name.includes(qLower);
                    });

                    // Also fetch song matches
                    songRes = await searchSongsByTitle(term);
                }

                if (abortRef.current.cancelled) return;

                const users = userRes.map(u => ({ type: 'user', data: u }));
                const songs = songRes.map(s => ({ type: 'song', data: s }));
                const combined = [...users, ...songs];
                setResults(combined);

                // Auto navigate if single clear user match
                try {
                    const q = term.trim().toLowerCase();
                    if (users.length === 1 && songs.length === 0 && q.length > 0) {
                        const u = users[0].data;
                        const nameLower = (u.nameLower || u.name || u.displayName || u.fullName || '').toLowerCase();
                        if (nameLower === q || (q.length >= 3 && nameLower.startsWith(q))) {
                            navigate(`/profile/${u.id}`);
                        }
                    }
                } catch (err) {
                    // ignore
                }
            } catch (e) {
                if (!abortRef.current.cancelled) console.error(e);
            } finally {
                if (!abortRef.current.cancelled) setLoading(false);
            }
        }, 300);

        return () => {
            clearTimeout(handler);
            abortRef.current.cancelled = true;
        };
    }, [term, liveUsers, navigate]);

    // Run search immediately (bypass debounce) — used by Enter key and search icon
    const runSearchNow = async () => {
        setLoading(true);
        try {
            let userRes = [];
            let songRes = [];

            if (!term.trim()) {
                userRes = liveUsers || [];
            } else {
                const qLower = term.toLowerCase();
                userRes = (liveUsers || []).filter(u => {
                    const name = (u.name || u.displayName || u.fullName || '').toLowerCase();
                    return name.includes(qLower);
                });
                songRes = await searchSongsByTitle(term);
            }

            const users = userRes.map(u => ({ type: 'user', data: u }));
            const songs = songRes.map(s => ({ type: 'song', data: s }));
            const combined = [...users, ...songs];
            setResults(combined);

            // Auto navigate on single match (same rules)
            try {
                const q = term.trim().toLowerCase();
                if (users.length === 1 && songs.length === 0 && q.length > 0) {
                    const u = users[0].data;
                    const nameLower = (u.nameLower || u.name || u.displayName || u.fullName || '').toLowerCase();
                    if (nameLower === q || (q.length >= 3 && nameLower.startsWith(q))) {
                        navigate(`/profile/${u.id}`);
                    }
                }
                // If not auto-navigating to a single profile, but term exists, open the results page
                if ((users.length !== 1 || songs.length !== 0) && term.trim().length > 0) {
                    navigate(`/results?q=${encodeURIComponent(term.trim())}`);
                }
            } catch (err) {
                // ignore navigation errors
            }
        } catch (err) {
            console.error('runSearchNow error', err);
        } finally {
            setLoading(false);
        }
    };



    return (
        <div className="search-users">
            <form onSubmit={(e) => { e.preventDefault(); runSearchNow(); }} className="search-form" aria-label="search form">
                <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Search users by name"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            runSearchNow();
                        }
                    }}
                    aria-label="search input"
                />
                <button
                    type="button"
                    onClick={runSearchNow}
                    aria-label="run search"
                    style={{ marginLeft: 8 }}
                >
                    🔍
                </button>


            </form>

            <div style={{ padding: '8px 12px', color: '#ccc' }}>
                Live users: {liveLoading ? 'loading...' : (liveUsers ? liveUsers.length : 0)}
            </div>

            {loading && <div>Searching...</div>}

            <ul className="search-results">
                {results.map((r, idx) => (
                    <li key={r.data.id || idx} className="result-item">
                        {r.type === 'user' ? (
                            <Link to={`/profile/${r.data.id}`}>
                                <div className="result-name">{r.data.name || r.data.displayName}</div>
                                <div className="result-bio">{r.data.bio}</div>
                            </Link>
                        ) : (
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    const targetUid = r.data.userId || r.data.id;
                                    if (targetUid) navigate(`/profile/${targetUid}`);
                                    else console.warn('No profile uid available for song result', r.data);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const targetUid = r.data.userId || r.data.id;
                                        if (targetUid) navigate(`/profile/${targetUid}`);
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="result-name">{r.data.title}</div>
                                <div className="result-bio">By {r.data.userId || r.data.id || 'Unknown'}</div>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SearchUsers;
